import { Box, Button, SxProps } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import OurBrandsExpandList from './OurBrandsExpandList';
import { AnimatePresence, motion } from 'framer-motion';
import { AboutYageoGroupOverviewOverviewContent } from '../../graphql/pimcore/modules/siteContent/types/AboutYageoGroupOverviewContent';
import { sanitizeRichText } from '../../graphql/pimcore/modules/siteContent/tools/sanitizeRichText';
import { LinkCollectionEntry } from '../../graphql/pimcore/modules/siteContent/types/SiteContentElement';
import { NEW_CS_BASE_URL as CS_BASE_URL } from '../../utils/ProjectUrls';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';
import { richTextToPlainTextLines } from '../../graphql/pimcore/modules/siteContent/tools/richTextToPlainTextLines';

export const buttonStyleProps: SxProps = {
    padding: '4px 11px',
    gap: '10px',
    height: '2.75rem',

    backgroundColor: '#E5673E',
    borderRadius: '5px',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '20px',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    transition: 'none',
    color: 'white',
    '&:hover': {
        background: 'linear-gradient(0deg, #B75332 0%, #E5673E 100%)',
    },
};

const OrangeTitle: SxProps = {
    fontFamily: 'Poppins',
    fontWeight: 700,
    fontSize: '16px',
    color: '#B75332',
    marginTop: '1.5rem',
};
const ReadMoreStyle: SxProps = {
    color: '#B75332',
    fontSize: '20px',
    fontWeight: '500',
    textTransform: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:hover': {
        backgroundColor: 'transparent',
    },
};

const splitHtmlFromThirdH3 = (html: string) => {
    const h3Regex = /<h3[^>]*>/g;
    let match;
    let count = 0;
    let index = -1;

    while ((match = h3Regex.exec(html)) !== null) {
        count++;
        if (count === 3) {
            index = match.index;
            break;
        }
    }

    if (index === -1) {
        return { main: html, readMore: '' };
    }

    return {
        main: html.slice(0, index),
        readMore: html.slice(index),
    };
};
const getLinkHref = (link?: LinkCollectionEntry): string => {
    if (!link) return '#';
    return link.kind === 'externalLinkFieldCollection' ? link.linkUrl : link.linkRoute;
};

interface OverviewPageProps {
    cmsContent: AboutYageoGroupOverviewOverviewContent;
}
interface AccordionItem {
    label: string;
    content: string[];
    imgSrc: string;
    link: string | null;
}

const YageoGroupOverviewPage: FunctionComponent<OverviewPageProps> = ({ cmsContent }) => {
    const { isScreenLg, isScreenMd, isScreenSm, isScreenXs } = useResponsive();
    const [isExpanded, setIsExpanded] = useState(false);
    const { responsiveWidth } = useResponsive();
    const { contentTree: cmsCommonContent } = useCommonContent();
    const linkLeftButton = cmsContent.links.links[0];
    const linkRightButton = cmsContent.links.links[1];

    const sanitized = sanitizeRichText(
        cmsContent.mainText.content,
        ['ul', 'li', 'a', 'h3'],
        ['href']
    );
    const { main: mainHtml, readMore: readMoreHtml } = splitHtmlFromThirdH3(sanitized);

    const brandList: AccordionItem[] = cmsContent.Brands.brands.entries.map((brand) => {
        return {
            label: brand.name,
            content: richTextToPlainTextLines(brand.text),
            imgSrc: brand.logoUrl,
            link: brand.externalLinkUrl,
        };
    });

    const LayoutStyles: SxProps = {
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '4rem',
        paddingBottom: '4rem',
        flexDirection: 'row',
        overflowY: 'hidden',
        width: responsiveWidth,
        alignItems: 'flex-start',
        margin: 'auto',
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0',
                    width: '100%',
                    boxSizing: 'border-box',
                    flexDirection: 'column',
                    overflowY: 'hidden',
                    backgroundColor: 'white',
                }}
            >
                <Box sx={{ ...LayoutStyles }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '0',
                            width: '100%',
                            boxSizing: 'border-box',
                            flexDirection: 'column',
                            overflowY: 'hidden',
                            backgroundColor: 'white',
                        }}
                    >
                        {isScreenMd || isScreenSm || isScreenXs ? (
                            <Box
                                sx={{
                                    width: isScreenLg ? '650px' : isScreenXs ? '295px' : '422px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    paddingTop: '2rem',
                                    flexDirection: 'column',
                                    overflowY: 'hidden',
                                    alignItems: 'center',
                                    margin: 'auto',
                                }}
                            >
                                <img
                                    src={cmsContent.mainImage.imageUrl}
                                    width="650px"
                                    height="298px"
                                    alt="Investor Relations"
                                    style={{ borderRadius: '8px' }}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        marginTop: '2rem',
                                        marginBottom: '2rem',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '1rem',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '1rem',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <CorporatePresentationButton />
                                            <CorporateVideo />
                                        </Box>
                                        <Box
                                            sx={{
                                                '& h3': {
                                                    ...OrangeTitle,
                                                },
                                            }}
                                        >
                                            <div dangerouslySetInnerHTML={{ __html: mainHtml }} />
                                        </Box>
                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{
                                                        duration: 0.5,
                                                        ease: 'easeInOut',
                                                    }}
                                                    style={{ overflow: 'hidden' }}
                                                >
                                                    <Box
                                                        sx={{
                                                            '& h3': {
                                                                ...OrangeTitle,
                                                            },
                                                        }}
                                                    >
                                                        <div
                                                            dangerouslySetInnerHTML={{
                                                                __html: readMoreHtml,
                                                            }}
                                                        />
                                                    </Box>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                            <Button
                                                onClick={() => setIsExpanded(!isExpanded)}
                                                sx={{ ...ReadMoreStyle }}
                                            >
                                                {isExpanded ? (
                                                    <>
                                                        {
                                                            cmsCommonContent.misc
                                                                .readLessButtonLabel.content
                                                        }{' '}
                                                        <ExpandLess sx={{ fontSize: '30px' }} />
                                                    </>
                                                ) : (
                                                    <>
                                                        {
                                                            cmsCommonContent.misc
                                                                .readMoreButtonLabel.content
                                                        }{' '}
                                                        <ExpandMore sx={{ fontSize: '30px' }} />
                                                    </>
                                                )}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    paddingTop: '1rem',
                                    paddingBottom: '2rem',
                                    flexDirection: 'row',
                                    overflowY: 'hidden',
                                    backgroundColor: 'white',
                                    width: responsiveWidth,
                                    alignItems: 'flex-start',
                                    margin: 'auto',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '120%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        fontFamily: 'Poppins',
                                        fontSize: '16px',
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        textAlign: 'left',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            '& h3': {
                                                ...OrangeTitle,
                                            },
                                        }}
                                    >
                                        <div dangerouslySetInnerHTML={{ __html: mainHtml }} />
                                    </Box>
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.5, ease: 'easeInOut' }}
                                                style={{ overflow: 'hidden' }}
                                            >
                                                <Box
                                                    sx={{
                                                        '& h3': {
                                                            ...OrangeTitle,
                                                        },
                                                    }}
                                                >
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: readMoreHtml,
                                                        }}
                                                    />
                                                </Box>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                        <Button
                                            onClick={() => setIsExpanded(!isExpanded)}
                                            sx={{ ...ReadMoreStyle }}
                                        >
                                            {isExpanded ? (
                                                <>
                                                    {
                                                        cmsCommonContent.misc.readLessButtonLabel
                                                            .content
                                                    }{' '}
                                                    <ExpandLess sx={{ fontSize: '30px' }} />
                                                </>
                                            ) : (
                                                <>
                                                    {
                                                        cmsCommonContent.misc.readMoreButtonLabel
                                                            .content
                                                    }{' '}
                                                    <ExpandMore sx={{ fontSize: '30px' }} />
                                                </>
                                            )}
                                        </Button>
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minWidth: '482px',
                                    }}
                                >
                                    {cmsContent.mainImage?.imageUrl && (
                                        <img
                                            src={cmsContent.mainImage.imageUrl}
                                            width="369px"
                                            height="281px"
                                            alt="Yageo Group"
                                            style={{ borderRadius: '8px' }}
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src =
                                                    '/fallback-image.png';
                                            }}
                                        />
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '1rem',
                                        }}
                                    >
                                        <CorporatePresentationButton />
                                        <CorporateVideo />
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0',
                    width: '100%',
                    boxSizing: 'border-box',
                    flexDirection: 'column',
                    overflowY: 'hidden',
                    backgroundColor: '#F8F8F8',
                }}
            >
                <Box sx={{ ...LayoutStyles, marginBottom: '2rem' }}>
                    <OurBrandsExpandList items={brandList} cmsContent={cmsContent.Brands} />
                </Box>
            </Box>
        </>
    );

    function CorporateVideo() {
        return (
            <Button
                variant="contained"
                sx={{ ...buttonStyleProps }}
                onClick={() => window.open(`${CS_BASE_URL}${getLinkHref(linkRightButton)}`)}
            >
                {cmsContent.links.links[1]?.linkText}
            </Button>
        );
    }

    function CorporatePresentationButton() {
        return (
            <Button
                variant="contained"
                sx={{ ...buttonStyleProps }}
                onClick={() => window.open(`${CS_BASE_URL}${getLinkHref(linkLeftButton)}`)}
            >
                {cmsContent.links.links[0]?.linkText}
            </Button>
        );
    }
};

export default YageoGroupOverviewPage;
